/* eslint-disable max-len */
import { List, useLocaleState, useRefresh, useTranslate } from "react-admin";
import { getFilterValuesFromInterval } from "@react-admin/ra-calendar";
import * as User from "providers/user";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  useMediaQuery,
  Theme
} from "@mui/material";
import AvailabilityCreate from "./AvailabilityCreate";
import AvailabilityEdit from "./AvailabilityEdit";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useLocation } from "react-router-dom";
import CalendarArtistsFilter from "./CalendarArtistsFilter";
import CustomCalendar from "./CustomCalendar";
import { getZonedDate, setFilterForAvailability } from "../utils/helpers";

const eventColors = [
  "#C0E7F8",
  "#F8D1C0",
  "#E3C0F8",
  "#F8ECC0",
  "#BEF3E0",
  "#C0C6F8",
  "#F8DEC0",
  "#F8C0E5",
  "#CFF8C0",
  "#7EC1DE",
  "#EDB59D",
  "#BA7EDE",
  "#F0C899",
  "#7ED9B8",
  "#7E88DE",
  "#DE7EBD",
  "#AADF98"
];
export const eventTextColors = [
  "#0D5675",
  "#752D0D",
  "#4D0D75",
  "#755E0D",
  "#0D754F",
  "#0D1775",
  "#75440D",
  "#750D52",
  "#266E0C",
  "#0A435C",
  "#5C230A",
  "#FFFFFF",
  "#5C360A",
  "#095237",
  "#FFFFFF",
  "#FFFFFF",
  "#1D5409"
];

export default function AvailabilityList() {
  const { user, permissions } = User.useUser();
  const [localeState] = useLocaleState();
  const translate = useTranslate();
  const refresh = useRefresh();
  const location = useLocation();
  const isSmall = useMediaQuery<Theme>("(max-width:915px)");

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editDialogId, setEditDialogId] = useState<string>("");
  const [selectedRange, setSelectedRange] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [forceLoading, setForceLoading] = useState<boolean>(true);
  const [artistFilter, setArtistFilter] = useState<string>(
    permissions?.isArtist ? permissions?.role?.user_id : ""
  );
  const [filter, setFilter] = useState<any>(
    setFilterForAvailability(selectedDate, permissions?.companyId)
  );
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      // this is quite a "hacking" solution but we need it, because
      // if you reload page and calendar is rendered immediately,
      // it causes drag end drop problem
      // (the event is duplicated on drag and is updated after 5-10 sec)
      setForceLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (window.location.search) {
      // react admin or calendar is adding some long queries in url which is
      // doing nothing (just an ugly long url),
      // because of that I'm clearing url. Yes, the browser's back
      // button will not do anything until you will click
      // enough times to go back, but it is not because of this, without
      // clearing url the back button would do the same
      // but would be changing random long queries until you really go back.
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [location]);

  useEffect(() => {
    setFilter(setFilterForAvailability(selectedDate, permissions?.companyId));
  }, [selectedDate, permissions?.companyId]);

  const converter = (event: any) => {
    const start = getZonedDate(event.start_time, event.location.timezone);
    const end = getZonedDate(event.end_time, event.location.timezone);
    const artistFullNames = permissions?.isArtist
      ? `${user.first_name} ${user.last_name}`
      : event.artists
          ?.map(
            ({ artist }: { artist: any }) =>
              `${artist.user.first_name} ${artist.user.last_name}`
          )
          .join(", ");
    const artistsIds = event.artists
      ?.map(({ artist }: { artist: any }) => `${artist.user.id}`)
      .join(", ");
    const title = `${artistFullNames}`;
    const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const retval = {
      id: String(event.id),
      title,
      start: start.toISOString(),
      end: end.toISOString(),
      extendedProps: {
        artistsIds,
        artistsIdsArray: event.artists?.map((item: any) => item.artist.id),
        artistsAvailabilityIdsArray: event.artists?.map((item: any) => item.id),
        locationId: event.location_id,
        duration: durationInMinutes,
        time: `${start.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })} - ${end.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })}`
      },
      backgroundColor:
        eventColors?.[locationIds?.indexOf(event?.location_id)] || "#F8D1C0",
      borderColor:
        eventColors?.[locationIds?.indexOf(event?.location_id)] || "#F8D1C0",
      textColor:
        eventTextColors?.[locationIds?.indexOf(event?.location_id)] ||
        "#752D0D",
      editable: permissions?.isArtist ? false : true,
      durationEditable: permissions?.isArtist ? false : true,
      display: "block",
      duration: durationInMinutes,
      start_time: start,
      end_time: end,
      recurring: event.recurring
    };
    return retval;
  };

  const handleSelectRange = (arg: any) => {
    setSelectedRange({ start_time: arg.start, end_time: arg.end });
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setSelectedRange(null);
  };
  const closeCreateDialogAndRefresh = () => {
    setCreateDialogOpen(false);
    setSelectedRange(null);
    refresh();
  };
  const closeEditDialogAndRefresh = () => {
    setEditDialogId("");
    refresh();
  };

  if (forceLoading) return <div />;

  return (
    <List
      filterDefaultValues={getFilterValuesFromInterval()}
      perPage={10000}
      pagination={false}
      filter={filter}
      actions={<div style={{ height: "0px" }} />}
      empty={false}
      sx={{
        "& .MuiBox-root": {
          padding: 0
        },
        "& .MuiToolbar-root": {
          minHeight: "0px",
          height: "0px"
        },
        "& .RaList-content": {
          backgroundColor: "#F6F4F1",
          padding: 0,
          boxShadow: "none"
        },
        "& .customListActionsButtonsWrapper .MuiButton-sizeSmall": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "36px",
          backgroundColor: "#FAF9F7",
          color: "rgba(48, 47, 46, 0.95)",
          fontSize: "14px",
          lineHeight: "18px",
          fontWeight: 500,
          borderRadius: "18px",
          padding: "0px 16px",
          border: "1px solid rgba(48, 47, 46, 0.23)",
          textTransform: "none"
        }
      }}
    >
      <CalendarArtistsFilter
        artistFilter={artistFilter}
        setArtistFilter={setArtistFilter}
        permissions={permissions}
      />
      <CustomCalendar
        locale={localeState}
        convertToEvent={converter}
        select={handleSelectRange}
        editable={true}
        selectable={true}
        setEditDialogId={setEditDialogId}
        artistFilter={artistFilter}
        type={"availability"}
        pageTitle={translate("navigation.Availability")}
        setSelectedDate={setSelectedDate}
      />
      {!permissions.isArtist && (
        <>
          {createDialogOpen && (
            <Dialog
              open={createDialogOpen}
              onClose={closeCreateDialog}
              sx={{
                "& .MuiDialogContent-root": {
                  padding: "0px"
                },
                "& .MuiDialog-paper": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  maxWidth: "410px",
                  width: "100%"
                }
              }}
            >
              <DialogTitle
                sx={{ display: "flex", padding: "4px 16px 0px 16px" }}
              >
                <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
                  {translate("availability.create_availability")}
                </div>
                <IconButton onClick={closeCreateDialog}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <AvailabilityCreate
                  selectedStart={selectedRange?.start_time?.toISOString()}
                  selectedEnd={selectedRange?.end_time?.toISOString()}
                  closeCreateDialogAndRefresh={closeCreateDialogAndRefresh}
                  setLoading={setLoading}
                  loading={loading}
                />
              </DialogContent>
            </Dialog>
          )}
          {!!editDialogId && (
            <Dialog
              open={!!editDialogId}
              onClose={() => setEditDialogId("")}
              sx={{
                "& .MuiDialogContent-root": {
                  padding: "0px"
                },
                "& .MuiDialog-paper": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  maxWidth: "410px",
                  width: "100%"
                }
              }}
            >
              <DialogTitle
                sx={{ display: "flex", padding: "4px 16px 0px 16px" }}
              >
                <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
                  {translate("availability.edit_availability")}
                </div>
                <IconButton onClick={() => setEditDialogId("")}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <AvailabilityEdit
                  editDialogId={editDialogId}
                  closeEditDialogAndRefresh={closeEditDialogAndRefresh}
                  loading={loading}
                  setLoading={setLoading}
                />
              </DialogContent>
            </Dialog>
          )}
          {!isSmall && (
            <Button
              onClick={() => setCreateDialogOpen(true)}
              disableRipple
              endIcon={<AddIcon />}
              className="!absolute !flex !justify-center !items-center !bg-mono-white height-[40px] !rounded-[20px] !text-[14px] !text-[#000] !font-['Inter'] !px-[12px]"
              style={{
                top: user.impersonate_company_id ? "48px" : "8px",
                left: "50%",
                transform: "translateX(-50%)",
                boxShadow:
                  " 0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)"
              }}
              guide-id="add-availability-button"
            >
              {translate("availability.new_availability")}
            </Button>
          )}
          {isSmall && (
            <IconButton
              onClick={() => setCreateDialogOpen(true)}
              guide-id="add-availability-button"
              sx={{
                position: "absolute",
                top: user.impersonate_company_id ? "48px" : "8px",
                left: "0px",
                backgroundColor: "#ffffff",
                color: "#000000",
                width: "36px",
                height: "36px",
                borderRadius: "18px",
                "@media screen and (max-width:430px)": {
                  left: "8px"
                }
              }}
            >
              <AddIcon />
            </IconButton>
          )}
        </>
      )}
    </List>
  );
}
